/**
 * Orchestrator API client for connecting to the backend.
 * 
 * Provides:
 * - WebSocket connection for real-time streaming (JWT via ?token= query param)
 * - REST fallback for sync operations (JWT via Authorization header)
 * - Event handling with callbacks
 * - Request ID extraction from backend responses
 */

import { getToken, clearToken } from './tokenStore';
import { apiFetch } from './apiFetch';
import { createLogger } from '../utils/logger';
import { isTokenExpired } from '../context/AuthContext';

const log = createLogger('OrchestratorClient');

// In production VITE_BACKEND_URL is the absolute backend URL (different Container App).
// In development the Vite dev-server proxy handles relative paths.
const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL && import.meta.env.VITE_BACKEND_URL !== '/')
  ? import.meta.env.VITE_BACKEND_URL.replace(/\/$/, '')
  : '';

// WebSocket: use backend URL if set, otherwise same origin (dev proxy).
const WS_URL = BACKEND_URL
  ? BACKEND_URL.replace(/^https?/, (m) => (m === 'https' ? 'wss' : 'ws'))
  : (typeof window !== 'undefined')
    ? window.location.origin.replace(/^http/, 'ws')
    : 'ws://localhost:3000';

/**
 * Event types from the orchestrator
 */
export const EventType = {
  THINK: 'think',
  ACT: 'act',
  OBSERVE: 'observe',
  DELEGATE: 'delegate',
  STREAM: 'stream',
  ANSWER: 'answer',
  ERROR: 'error',
  STATUS: 'status',
  CLARIFY: 'clarify',
};

/**
 * WebSocket-based orchestrator client for real-time events
 */
export class OrchestratorClient {
  constructor(clientId) {
    this.clientId = clientId || `client-${Date.now()}`;
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnects = 5;
    this.connected = false;
    this._pingInterval = null;
  }

  /**
   * Connect to the orchestrator WebSocket
   */
  connect() {
    return new Promise((resolve, reject) => {
      const token = getToken();

      // Pre-flight: if the stored token is expired, clear it and redirect to
      // re-authenticate rather than dialing a connection that will be rejected.
      if (token && isTokenExpired(token)) {
        log.warn('ws_token_expired_preflight');
        clearToken();
        window.location.replace('/');
        return reject(new Error('Token expired'));
      }

      const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
      const wsUrl = `${WS_URL}/ws/${this.clientId}${tokenQuery}`;
      console.log('[Orchestrator] Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));
      log.info('ws_connecting', { clientId: this.clientId });

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[Orchestrator] Connected');
        log.info('ws_open', { clientId: this.clientId });
        this.connected = true;
        this.reconnectAttempts = 0;
        // Send a ping every 25 seconds to keep the connection alive through
        // Azure Container Apps' 30-second idle WebSocket timeout
        this._pingInterval = setInterval(() => {
          if (this.connected && this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 25000);
        resolve();
      };

      this.ws.onclose = (event) => {
        console.log('[Orchestrator] Disconnected:', event.code, event.reason);
        log.warn('ws_close', { code: event.code, reason: event.reason });
        this.connected = false;
        clearInterval(this._pingInterval);
        this._pingInterval = null;

        // 4001 = auth rejected by server — clear token and redirect to login
        if (event.code === 4001) {
          clearToken();
          window.location.replace('/');
          return;
        }

        this._handleDisconnect();
      };

      this.ws.onerror = (error) => {
        console.error('[Orchestrator] WebSocket error:', error);
        log.error('ws_error', { error: error?.message || 'unknown' });
        reject(error);
      };

      this.ws.onmessage = (event) => {
        this._handleMessage(event.data);
      };
    });
  }

  /**
   * Disconnect from the orchestrator
   */
  disconnect() {
    clearInterval(this._pingInterval);
    this._pingInterval = null;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
  }

  /**
   * Send a query to the orchestrator.
   *
   * @param {string} question   - The user's message
   * @param {string|null} queryId  - Optional query ID (generated if null)
   * @param {string} userNtid   - Authenticated user's NTID (empty string if unknown)
   * @param {string} conversationId - Conversation ID for multi-turn context
   */
  sendQuery(question, queryId = null, userNtid = '', conversationId = '') {
    if (!this.connected || !this.ws) {
      console.error('[Orchestrator] Not connected, cannot send query');
      log.warn('send_query_not_connected');
      return false;
    }

    const message = {
      question,
      query_id: queryId || `q-${Date.now()}`,
      user_ntid: userNtid,
      conversation_id: conversationId,
    };

    this.ws.send(JSON.stringify(message));
    return message.query_id;
  }

  /**
   * Send clarification answers back to the orchestrator.
   */
  sendClarifyResponse(queryId, answers) {
    if (!this.connected || !this.ws) {
      console.error('[Orchestrator] Not connected, cannot send clarify_response');
      log.warn('send_clarify_not_connected');
      return false;
    }
    this.ws.send(JSON.stringify({
      type: 'clarify_response',
      query_id: queryId,
      answers,
    }));
    return true;
  }

  /**
   * Register an event listener
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  /**
   * Remove an event listener
   */
  off(eventType, callback) {
    if (this.listeners.has(eventType)) {
      const callbacks = this.listeners.get(eventType);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Handle incoming WebSocket message
   */
  _handleMessage(data) {
    try {
      const event = JSON.parse(data);
      // Silently discard keepalive pong responses
      if (event.type === 'pong') return;
      console.log('[Orchestrator] Event:', event.type, event);
      log.debug('ws_event', { type: event.type, queryId: event.query_id, requestId: event.request_id });

      // Notify listeners
      if (this.listeners.has(event.type)) {
        for (const callback of this.listeners.get(event.type)) {
          callback(event);
        }
      }

      // Also notify catch-all listeners
      if (this.listeners.has('*')) {
        for (const callback of this.listeners.get('*')) {
          callback(event);
        }
      }
    } catch (e) {
      console.error('[Orchestrator] Failed to parse message:', e);
      log.error('ws_parse_error', { error: e?.message });
    }
  }

  /**
   * Handle disconnection with auto-reconnect
   */
  _handleDisconnect() {
    // Never retry if the token is gone or expired — the pre-flight check in
    // connect() will redirect to re-auth anyway, so retrying just wastes cycles.
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      log.warn('ws_no_valid_token_abort_reconnect');
      return;
    }

    if (this.reconnectAttempts < this.maxReconnects) {
      this.reconnectAttempts++;
      const delay = 1000 * Math.pow(2, this.reconnectAttempts);
      console.log(`[Orchestrator] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
      log.info('ws_reconnecting', { attempt: this.reconnectAttempts, delayMs: delay });

      setTimeout(() => {
        this.connect().catch((e) => {
          console.error('[Orchestrator] Reconnect failed:', e);
          log.error('ws_reconnect_failed', { attempt: this.reconnectAttempts, error: e?.message });
        });
      }, delay);
    } else {
      console.error('[Orchestrator] Max reconnect attempts reached');
      log.error('ws_max_reconnects', { maxReconnects: this.maxReconnects });
    }
  }
}

/**
 * REST API client for synchronous operations.
 * All methods use apiFetch which injects JWT auth headers
 * and handles 401 responses automatically.
 */
export const api = {
  /**
   * Get all agents
   */
  async getAgents() {
    const response = await apiFetch(`${BACKEND_URL}/agents`);
    if (!response.ok) throw new Error(`Failed to fetch agents: ${response.statusText}`);
    return response.json();
  },

  /**
   * Get a specific agent
   */
  async getAgent(agentId) {
    const response = await apiFetch(`${BACKEND_URL}/agents/${agentId}`);
    if (!response.ok) throw new Error(`Failed to fetch agent: ${response.statusText}`);
    return response.json();
  },

  /**
   * Health check
   */
  async healthCheck() {
    const response = await apiFetch(`${BACKEND_URL}/health`);
    if (!response.ok) throw new Error(`Health check failed: ${response.statusText}`);
    return response.json();
  },

  /**
   * Execute query synchronously (no streaming)
   */
  async query(question, queryId = null) {
    const response = await apiFetch(`${BACKEND_URL}/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, query_id: queryId }),
    });
    if (!response.ok) throw new Error(`Query failed: ${response.statusText}`);
    return response.json();
  },

  /**
   * Stream query via SSE (alternative to WebSocket)
   */
  async *streamQuery(question, queryId = null) {
    const response = await apiFetch(`${BACKEND_URL}/query/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, query_id: queryId }),
    });

    if (!response.ok) throw new Error(`Stream query failed: ${response.statusText}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          yield JSON.parse(data);
        }
      }
    }
  },
};

// Singleton client instance
let clientInstance = null;

/**
 * Get or create the orchestrator client
 */
export function getClient(clientId = null) {
  if (!clientInstance) {
    clientInstance = new OrchestratorClient(clientId);
  }
  return clientInstance;
}

export default OrchestratorClient;
